import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { groupByColumn } from './logic.ts';
import { StateView } from './StateView.tsx';

export interface BoardColumn {
  id: string;
  title: ReactNode;
}

/**
 * Quadro (ADR 0128): itens em colunas, uma por etapa. Cada coluna mostra quantos tem; o cartão é
 * de quem chama (título, valor, responsável). Mudar de etapa é um botão no cartão ou no item
 * aberto: arrastar não é a única forma (teclado e celular).
 */
export function Board<T>({
  columns,
  items,
  columnOf,
  keyOf,
  renderCard,
  label,
}: {
  columns: readonly BoardColumn[];
  items: readonly T[];
  columnOf: (item: T) => string;
  keyOf: (item: T) => string;
  renderCard: (item: T) => ReactNode;
  /** Nome do quadro para leitores de tela. */
  label: string;
}) {
  const { t } = useTranslation();
  const grouped = groupByColumn(
    items,
    columns.map((c) => c.id),
    columnOf,
  );
  return (
    <div
      role="region"
      aria-label={label}
      tabIndex={0}
      className="flex snap-x gap-3 overflow-x-auto pb-2"
    >
      {columns.map((col) => {
        const list = grouped.get(col.id) ?? [];
        return (
          <section
            key={col.id}
            aria-label={typeof col.title === 'string' ? col.title : undefined}
            className="flex w-72 shrink-0 snap-start flex-col gap-2 rounded-lg border border-rule bg-paper p-2"
          >
            <header className="flex items-center justify-between px-1.5 pt-1 text-sm font-medium">
              <span className="truncate">{col.title}</span>
              <span className="text-xs text-muted tabular-nums">{list.length}</span>
            </header>
            {list.length === 0 ? (
              <p className="px-1.5 py-4 text-center text-xs text-muted">
                {t('patterns.board.emptyColumn')}
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {list.map((item) => (
                  <li key={keyOf(item)} className="ui-panel rounded-md bg-sheet p-3 text-sm">
                    {renderCard(item)}
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}

/**
 * Galeria (ADR 0128): cartões em grade que se ajusta à largura. O cartão traz a imagem (ou a
 * inicial), o título, uma linha de apoio e, se quiser, uma etiqueta e o botão principal.
 */
export function CardGrid<T>({
  items,
  keyOf,
  renderCard,
  empty,
}: {
  items: readonly T[];
  keyOf: (item: T) => string;
  renderCard: (item: T) => ReactNode;
  /** O estado vazio (em geral um `StateView` com o botão de cadastrar). */
  empty?: ReactNode;
}) {
  if (items.length === 0) return <>{empty ?? <StateView kind="empty" />}</>;
  return (
    <ul className="grid grid-cols-[repeat(auto-fill,minmax(14rem,1fr))] gap-3">
      {items.map((item) => (
        <li
          key={keyOf(item)}
          className="ui-panel flex flex-col overflow-hidden rounded-lg bg-sheet"
        >
          {renderCard(item)}
        </li>
      ))}
    </ul>
  );
}

/** Topo do cartão da galeria: a imagem, ou a inicial no tom do destaque quando não há imagem. */
export function CardCover({ src, name }: { src?: string; name: string }) {
  return src ? (
    <img src={src} alt="" className="aspect-[4/3] w-full object-cover" />
  ) : (
    <span
      aria-hidden
      className="flex aspect-[4/3] w-full items-center justify-center bg-brand-soft text-3xl font-semibold text-brand-text"
    >
      {name.trim().charAt(0).toUpperCase()}
    </span>
  );
}
